import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { X, ArrowRight } from 'lucide-react';
import { trackEvent, EVENTS } from '@/lib/analytics';

/**
 * A single conversion prompt, aimed at the primary business (contractor growth)
 * rather than a newsletter. It asks one question and routes to the page that
 * answers it, so it is an entry point and not a second lead form.
 *
 * Deliberately restrained: it never appears on load, it appears at most once per
 * visitor per month, and dismissing it is remembered.
 */

const STORAGE_KEY = 'hm_growth_prompt_dismissed_at';
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000; // a month
const MIN_DWELL_MS = 20000;
const SCROLL_DEPTH = 0.55;

const GOALS = [
  { id: 'leads', label: 'More qualified leads', to: '/contractors/get-qualified-leads' },
  { id: 'google', label: 'Google Ads', to: '/contractors/google-ads' },
  { id: 'meta', label: 'Meta Ads', to: '/contractors/meta-ads' },
  { id: 'tiktok', label: 'TikTok Ads', to: '/services/social-media-marketing' },
  { id: 'seo', label: 'SEO', to: '/contractors/seo' },
  { id: 'website', label: 'My website', to: '/contractors/web-design' },
  { id: 'everything', label: 'The full growth system', to: '/contractors/grow-your-business' },
];

/* Storage can throw in private windows, so every access is guarded and the
   prompt simply behaves as if it has never been dismissed. */
const recentlyDismissed = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return !!raw && Date.now() - Number(raw) < SNOOZE_MS;
  } catch {
    return false;
  }
};
const rememberDismissal = () => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    /* nothing to do: the prompt just may appear again next visit */
  }
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default function GrowthPrompt() {
  const [open, setOpen] = useState(false);
  const [goal, setGoal] = useState(null);
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);
  const firedRef = useRef(false);

  const show = useCallback(() => {
    if (firedRef.current || recentlyDismissed()) return;
    firedRef.current = true;
    returnFocusRef.current = document.activeElement;
    setOpen(true);
    trackEvent(EVENTS.growthPromptShown, { location: 'home' });
  }, []);

  const close = useCallback(
    (reason) => {
      setOpen(false);
      rememberDismissal();
      trackEvent(EVENTS.growthPromptDismiss, { reason });
      const el = returnFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    },
    []
  );

  useEffect(() => {
    if (recentlyDismissed()) return undefined;
    const started = Date.now();

    // Both triggers are registered: exit intent is the better signal where there
    // is a pointer, scroll depth covers everyone else, and `show` only ever
    // fires once. Neither counts until the visitor has had time to read.
    const onMouseOut = (e) => {
      if (Date.now() - started < MIN_DWELL_MS) return;
      if (e.clientY <= 0 && !e.relatedTarget) show();
    };
    const onScroll = () => {
      if (Date.now() - started < MIN_DWELL_MS) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max > SCROLL_DEPTH) show();
    };

    document.addEventListener('mouseout', onMouseOut);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('scroll', onScroll);
    };
  }, [show]);

  // Modal behaviour: escape, focus trap, and a locked background.
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close('escape');
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes || !nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const id = window.setTimeout(() => {
      panelRef.current?.querySelector(FOCUSABLE)?.focus();
    }, 60);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(id);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  const onContinue = () => {
    const chosen = GOALS.find((g) => g.id === goal);
    if (!chosen) return;
    rememberDismissal();
    trackEvent(EVENTS.growthPromptContinue, { goal: chosen.id });
    setOpen(false);
    navigate(chosen.to);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-end justify-center p-4 sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0 : 0.25 }}
          data-testid="growth-prompt"
        >
          <div
            className="absolute inset-0 bg-ink/70"
            onClick={() => close('backdrop')}
            aria-hidden="true"
          />

          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="growth-prompt-title"
            aria-describedby="growth-prompt-desc"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
            transition={{ duration: reduce ? 0 : 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-md bg-navy px-6 py-8 sm:px-10 sm:py-11 shadow-[0_40px_100px_rgba(5,5,7,0.6)]"
          >
            <button
              onClick={() => close('close_button')}
              aria-label="Close"
              data-testid="growth-prompt-close"
              className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-md text-silver transition-colors hover:bg-white/10 hover:text-bone"
            >
              <X className="h-5 w-5" />
            </button>

            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-silver">
              Before you go
            </p>
            <h2
              id="growth-prompt-title"
              className="mt-4 pr-10 font-display text-2xl sm:text-3xl font-medium tracking-tight leading-snug text-bone"
            >
              What would you most like to improve?
            </h2>
            <p id="growth-prompt-desc" className="mt-3 text-[15px] leading-relaxed text-silver">
              Pick one and we'll take you straight to it. No form here.
            </p>

            <div className="mt-7 flex flex-wrap gap-2.5" role="radiogroup" aria-labelledby="growth-prompt-title">
              {GOALS.map((g) => {
                const on = goal === g.id;
                return (
                  <button
                    key={g.id}
                    role="radio"
                    aria-checked={on}
                    data-testid={`growth-prompt-goal-${g.id}`}
                    onClick={() => {
                      setGoal(g.id);
                      trackEvent(EVENTS.growthPromptGoal, { goal: g.id });
                    }}
                    className={`rounded-md border px-4 py-3 text-[14px] font-bold transition-colors duration-200 ${
                      on
                        ? 'border-bone bg-bone text-navy'
                        : 'border-white/20 text-silver hover:border-white/50 hover:text-bone'
                    }`}
                  >
                    {g.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-white/12 pt-6">
              <button
                onClick={onContinue}
                disabled={!goal}
                data-testid="growth-prompt-continue"
                className="group inline-flex items-center justify-center gap-2.5 rounded-md bg-steel px-7 py-3.5 text-[15px] font-bold text-bone transition-colors duration-300 hover:bg-steel-light hover:text-navy disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-steel disabled:hover:text-bone"
              >
                Continue
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-disabled:translate-x-0" />
              </button>
              <button
                onClick={() => close('not_now')}
                data-testid="growth-prompt-dismiss"
                className="text-[14px] font-bold text-silver underline underline-offset-4 decoration-white/25 transition-colors hover:text-bone hover:decoration-bone"
              >
                Not right now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
